import { useEffect, useState } from 'react'
import { Loader2, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { useSendCreditNote } from '@/hooks/useInvoices'
import { getApiErrorMessage } from '@/services/api'
import { primaryEmail } from '@/lib/clientContacts'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { Client, CreditNote } from '@/types'

interface Props {
  creditNote: CreditNote | null
  fallbackClient?: Client | null
  onClose: () => void
  onSent?: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldClass = cn(
  'rounded-xl border-0 bg-[#f4f5f8] px-4 py-2.5 text-sm text-[#171a20] placeholder:text-[#8f95a6]',
  FORM_CONTROL_FOCUS_CLASS
)

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string
  children: string
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#d63b33]'>*</span>}
    </Label>
  )
}

function SummaryRow({
  label,
  value,
  bordered = true,
  emphasized = false,
}: {
  label: string
  value: string
  bordered?: boolean
  emphasized?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 py-2 text-sm leading-5',
        bordered && 'border-b border-[#e7e9ef]'
      )}
    >
      <span className='min-w-36 text-[#6c7285]'>{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-right font-medium',
          emphasized ? 'text-[#ff5a1f]' : 'text-[#171a20]'
        )}
      >
        {value}
      </span>
    </div>
  )
}

function resolveClientName(
  creditNote: CreditNote | null,
  fallbackClient: Client | null | undefined,
  fallbackLabel: string
) {
  return (
    creditNote?.client?.name ??
    creditNote?.client_name ??
    fallbackClient?.name ??
    fallbackLabel
  )
}

export function SendCreditNoteDialog({
  creditNote,
  fallbackClient,
  onClose,
  onSent,
}: Props) {
  const { t } = useTranslation('invoices')
  const sendCreditNote = useSendCreditNote()
  const open = !!creditNote
  const reference = creditNote?.number ?? creditNote?.id.slice(0, 8) ?? ''
  const clientName = resolveClientName(
    creditNote,
    fallbackClient,
    t('detail.invoiceSendDialog.unknownClient')
  )
  const totalLabel = formatCurrency(creditNote?.total ?? 0, 'EUR', 2)
  const defaultTo =
    creditNote?.client?.email ??
    primaryEmail(creditNote?.client) ??
    fallbackClient?.email ??
    primaryEmail(fallbackClient)
  const defaultSubject = t('creditNotes.sendSubject', {
    number: reference,
  }).trim()
  const defaultBody = t('creditNotes.sendBody', {
    amount: totalLabel,
    clientName,
    reference,
  })

  const [to, setTo] = useState(defaultTo ?? '')
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody)
  const [toError, setToError] = useState<string | null>(null)
  const [subjectError, setSubjectError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    /* eslint-disable react-hooks/set-state-in-effect */
    setTo(defaultTo ?? '')
    setSubject(defaultSubject)
    setBody(defaultBody)
    setToError(null)
    setSubjectError(null)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, defaultTo, defaultSubject, defaultBody])

  const submit = async () => {
    if (!creditNote) return
    if (!to || !EMAIL_RE.test(to.trim())) {
      setToError(t('creditNotes.sendDialog.invalidEmail'))
      return
    }
    if (!subject.trim()) {
      setSubjectError(t('creditNotes.sendDialog.requiredSubject'))
      return
    }

    try {
      await sendCreditNote.mutateAsync({
        id: creditNote.id,
        body: {
          recipient_email: to.trim(),
          message: body.trim() || undefined,
        },
      })
      toast({ variant: 'success', title: t('creditNotes.sendSuccess') })
      onClose()
      onSent?.()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('creditNotes.sendError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent
        className='max-h-[calc(100vh-1.5rem)] max-w-[640px] gap-0 overflow-hidden border-[#e7e9ef] p-0 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'
        showCloseButton={false}
      >
        <div className='max-h-[calc(100vh-1.5rem)] overflow-y-auto overflow-x-hidden px-6 py-6 sm:px-8 sm:py-8'>
          <div className='flex flex-col gap-6'>
            <DialogTitle className='pr-8 font-heading text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {t('creditNotes.sendTitle')}
            </DialogTitle>

            <div className='rounded-2xl bg-[#f4f5f8] px-4 py-2'>
              <SummaryRow
                label={t('creditNotes.sendDialog.client')}
                value={clientName}
              />
              <SummaryRow
                label={t('creditNotes.sendDialog.reference')}
                value={reference}
              />
              <SummaryRow
                label={t('creditNotes.sendDialog.totalInclVat')}
                value={totalLabel}
                bordered={false}
                emphasized
              />
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='credit-note-send-to' required>
                {t('creditNotes.sendDialog.to')}
              </FieldLabel>
              <Input
                id='credit-note-send-to'
                type='email'
                value={to}
                onChange={e => {
                  setTo(e.target.value)
                  setToError(null)
                }}
                className={cn(
                  fieldClass,
                  toError && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {toError && <p className='text-sm text-destructive'>{toError}</p>}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='credit-note-send-subject' required>
                {t('creditNotes.sendDialog.subject')}
              </FieldLabel>
              <Input
                id='credit-note-send-subject'
                value={subject}
                maxLength={200}
                onChange={e => {
                  setSubject(e.target.value)
                  setSubjectError(null)
                }}
                className={cn(
                  fieldClass,
                  subjectError && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {subjectError && (
                <p className='text-sm text-destructive'>{subjectError}</p>
              )}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='credit-note-send-body'>
                {t('creditNotes.sendDialog.message')}
              </FieldLabel>
              <Textarea
                id='credit-note-send-body'
                value={body}
                maxLength={2000}
                rows={8}
                onChange={e => setBody(e.target.value)}
                className={cn(
                  'mb-1 min-h-[204px] rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#8f95a6]',
                  FORM_CONTROL_FOCUS_CLASS
                )}
              />
              <p className='text-sm leading-[1.4] text-[#3d434e]'>
                {t('creditNotes.sendDialog.attachmentNote')}
              </p>
            </div>

            <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
              <Button
                type='button'
                variant='outline'
                className='h-10 rounded-xl border-[#e7e9ef] px-3 text-sm font-medium text-[#171a20] hover:bg-[#fbfbfc]'
                disabled={sendCreditNote.isPending}
                onClick={onClose}
              >
                {t('creditNotes.sendDialog.cancel')}
              </Button>
              <Button
                type='button'
                className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium text-white hover:bg-[#e6511c]'
                disabled={sendCreditNote.isPending}
                onClick={submit}
              >
                {sendCreditNote.isPending ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Send className='mr-2 size-4' />
                )}
                {t('creditNotes.sendDialog.sendNow')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
